'use client';

import { useEffect, useState } from 'react';
import type { ReadableSignal } from '@callixbrasil/client-sdk';

/**
 * Hook to convert a ReadableSignal from Callix SDK into React state.
 * Automatically subscribes to signal changes and updates the component.
 * 
 * @param signal - The ReadableSignal to observe
 * @returns The current value of the signal
 */
export function useSignal<T>(signal: ReadableSignal<T> | undefined): T | undefined {
  const [value, setValue] = useState<T | undefined>(signal?.get());

  useEffect(() => {
    if (!signal) {
      setValue(undefined);
      return;
    }

    // Set initial value
    setValue(signal.get());

    // Subscribe to changes
    const unsubscribe = signal.subscribe((newValue) => {
      setValue(newValue);
    });

    return unsubscribe;
  }, [signal]);

  return value;
}
